<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="750px" scrollable>
      <v-card flat>
        <v-toolbar elevation="0" v-if="!edit">
          <v-toolbar-title class="pl-3 text--subtitle">
            <v-icon class="mr-2" color="primary" style="padding-bottom: 2px">
              mdi-key
            </v-icon>
            {{ edit ? $t('message.edit') : $t('message.create') }}
            {{ $t('message.pages.drm.createKey.title').toLowerCase() }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="handleClose()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-toolbar v-else elevation="0" class="mb-2 mx-0 px-0">
          <v-tabs fixed-tabs v-model="keyTab">
            <v-tab v-for="item in keyTabOptions" :key="item.tab">
              {{ item.tab }}
            </v-tab>
          </v-tabs>
        </v-toolbar>
        <v-card-text
          class="pl-7 pr-5 py-0"
          :style="edit ? 'height: 380px' : 'height: 340px'"
        >
          <v-row v-if="keyTab === 0" no-gutters class="pt-4">
            <v-col cols="12">
              <v-text-field
                v-model="privateTokenObject.key"
                :disabled="edit"
                :label="$t('message.pages.drm.createKey.privateKey')"
                outlined
                dense
                color="primary"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="privateTokenObject.string1"
                :label="`${$t('message.pages.drm.createKey.field')} 1`"
                outlined
                dense
                color="primary"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="privateTokenObject.string2"
                :label="`${$t('message.pages.drm.createKey.field')} 2`"
                outlined
                dense
                color="primary"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="privateTokenObject.string3"
                :label="`${$t('message.pages.drm.createKey.field')} 3`"
                outlined
                dense
                color="primary"
              ></v-text-field>
            </v-col>
            <v-col cols="12" v-if="edit">
              <v-row no-gutters>
                <span class="mr-3"
                  ><b>{{ $t('message.pages.drm.createKey.canView') }}</b></span
                >
                <v-switch
                  v-model="privateTokenObject.status_key"
                  class="ml-4 mt-0 pt-0"
                  dense
                  hide-details
                ></v-switch>
              </v-row>
            </v-col>
            <v-col cols="12" class="mt-4" v-if="updateError">
              <v-alert dense outlined type="error" width="100%">
                <span>
                  {{
                    errMsg
                      ? errMsg
                      : $t('message.pages.qualitySettings.errors.defaultError')
                  }}.
                  {{ `${$t('message.commom.code')}: ${errCode}` }}
                </span>
              </v-alert>
            </v-col>
          </v-row>
          <v-row v-if="keyTab === 1" no-gutters>
            <v-row no-gutters v-if="metadataLoading">
              <v-col cols="12">
                <loading-logo :value="200" />
              </v-col>
            </v-row>
            <v-col
              cols="12"
              class="mt-5"
              v-else-if="
                privateTokenObject.logs && privateTokenObject.logs.length
              "
            >
              <v-row no-gutters class="pr-0 py-0">
                <v-col cols="2">
                  <span
                    ><b>{{ $t('message.pages.historyModal.column1') }}</b></span
                  >
                </v-col>
                <v-col cols="4">
                  <span
                    ><b>{{ $t('message.pages.historyModal.column2') }}</b></span
                  >
                </v-col>
                <v-col cols="6">
                  <span
                    ><b>{{
                      $t('message.pages.drm.createKey.relevantInfo')
                    }}</b></span
                  >
                </v-col>
              </v-row>
              <v-col cols="12" class="mx-0 px-0">
                <v-divider />
              </v-col>
              <v-card-text class="pa-0" style="height: 200px">
                <template>
                  <v-row
                    v-if="!privateTokenObject.logs.length"
                    class="d-flex flex-column mt-4"
                    no-gutters
                    justify="center"
                    align="center"
                  >
                    <span>
                      {{ $t('message.pages.drm.keyModal.noRelevantInfo') }}
                    </span>
                  </v-row>
                  <v-row
                    v-else
                    no-gutters
                    v-for="(log, i) in privateTokenObject.logs"
                    :key="i"
                  >
                    <v-col cols="2">
                      <span>{{ log.json.type.toUpperCase() }}</span>
                    </v-col>
                    <v-col cols="4">
                      <span>
                        {{ parseDate(log.date) }}
                        {{ $t('message.pages.drm.createKey.at') }}
                        {{ parseHours(log.date) }}</span
                      >
                    </v-col>
                    <v-col
                      cols="6"
                      v-if="log.json.webhook && log.json.webhook.customer"
                    >
                      <v-row no-gutters>
                        <v-col
                          cols="12"
                          v-for="(detail, i) in log.json.webhook.customer"
                          :key="detail"
                        >
                          <v-row no-gutters>
                            <v-col cols="2">
                              {{ capitalizeFirstLetter(i) }}:
                            </v-col>
                            <v-col cols="10">{{ detail }}</v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="5" v-else>
                      {{ $t('message.pages.drm.keyModal.noInfo') }}
                    </v-col>
                    <v-col cols="12" class="pa-0 py-2">
                      <v-divider />
                    </v-col>
                  </v-row>
                </template>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-5 py-4">
          <v-btn
            v-if="edit"
            text
            class="mr-4 px-4"
            color="error"
            :disabled="isFetching || !hasEditDrmPerm"
            :loading="isFetchingRemove"
            @click="handleRemove"
          >
            Remover chave
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="keyTab === 0"
            text
            class="mr-4 px-4"
            :disabled="isFetching || isFetchingRemove"
            @click="handleClose"
          >
            {{ $t('message.cancel') }}
          </v-btn>
          <v-btn
            v-if="keyTab === 0"
            large
            class="primary white--text"
            min-width="185px"
            @click="handleConfirm"
            :loading="isFetching"
            :disabled="
              isFetching ||
              isFetchingRemove ||
              !hasMinimumFieldsFilled ||
              !hasEditDrmPerm
            "
          >
            {{ $t('message.confirm') }}
          </v-btn>
          <v-btn v-else text min-width="185px" large @click="handleClose">
            {{ $t('message.actions.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import drmApi from '@/services/drmApi'
import i18n from '@/translations/lang'
import LoadingLogo from '@/components/LoadingLogo.vue'

export default {
  name: 'DrmCreateSecretKey',
  components: {
    LoadingLogo,
  },
  data: () => ({
    open: false,
    edit: false,
    isFetching: false,
    isFetchingRemove: false,
    privateTokenObject: {
      drm_group_id: '',
      string1: '',
      string2: '',
      string3: '',
      key: '',
      logs: [],
      status_key: true,
    },
    tokenId: '',
    drmGroup: {
      id: '',
    },
    metadataLoading: false,
    metadataSearched: false,
    keyTab: 0,
    keyTabOptions: [
      { tab: i18n.t('message.pages.drm.keyModal.tabOption') },
      { tab: 'Logs' },
    ],
    updateError: false,
    errMsg: '',
    errCode: '402',
  }),
  computed: {
    hasEditDrmPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-watermark') ??
        false
      )
    },
    hasMinimumFieldsFilled() {
      return (
        !!this.privateTokenObject.key &&
        (this.privateTokenObject.string1.length > 2 ||
          this.privateTokenObject.string2.length > 2 ||
          this.privateTokenObject.string3.length > 2)
      )
    },
  },
  watch: {
    open() {
      const refForm = this.$refs.form
      refForm && refForm.resetValidation()
    },
  },
  mounted() {
    this.$root.$on('openDrmSecretKey', (data) => {
      Object.assign(this.$data, this.initialState())
      this.edit = data.edit
      if (data.edit) {
        this.privateTokenObject = data.item
        this.tokenId = data.item.id
      } else {
        this.privateTokenObject.drm_group_id = data.group.id
      }
      this.open = true
      this.drmGroup = data.group
      this.getMetadata()
    })
  },
  beforeDestroy() {
    this.$root.$off('openDrmSecretKey')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['addUser', 'editUser', 'addFolder', 'editFolder']),
    handleCopyItem(item) {
      navigator.clipboard.writeText(`${JSON.stringify(item)}`)
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: 'Informações adicionais copiadas com sucesso!',
      })
    },
    capitalizeFirstLetter(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    async getMetadata() {
      this.metadataLoading = true
      try {
        let ret = await drmApi.getOneDrmPrivateToken(this.tokenId)
        this.privateTokenObject = ret.data
        this.metadataSearched = true
      } catch (e) {
        this.metadataLoading = false
        console.log('e', e)
      }
      this.metadataLoading = false
    },
    handleConfirm() {
      if (this.edit) {
        this.handleEdit()
      } else {
        this.handleCreate()
      }
    },
    async handleCreate() {
      this.isFetching = true
      this.trimFields()
      if (!this.metadataSearched) {
        delete this.privateTokenObject.logs
      }
      try {
        let ret = await drmApi.createDrmPrivateToken(this.privateTokenObject)
        this.$root.$emit('addNewPrivateToken', ret.data)
        this.handleClose()
      } catch (e) {
        console.log('e', e)
      }
      this.isFetching = false
    },
    async handleEdit() {
      this.updateError = false
      this.isFetching = true
      this.trimFields()
      try {
        let ret = await drmApi.drmUpdatePrivateToken(this.tokenId, {
          drm_group_id: this.privateTokenObject.drm_group_id,
          string1: this.privateTokenObject.string1,
          string2: this.privateTokenObject.string2,
          string3: this.privateTokenObject.string3,
          status_key: this.privateTokenObject.status_key,
        })
        this.$root.$emit('updatePrivateToken', ret.data)
        this.handleClose()
      } catch (e) {
        this.updateError = true
        if (e.response.status) this.errCode = e.response.status
        if (e.response.data.errMsg) this.errMsg = e.response.data.errMsg
      }
      this.isFetching = false
    },
    async handleRemove() {
      this.isFetchingRemove = true
      let arr = []
      arr.push({ id: this.privateTokenObject.id })
      try {
        await drmApi.deleteMultipleDrmPrivateToken(arr)
        this.$root.$emit('removePrivateTokens', arr)
        this.handleClose()
      } catch (e) {
        console.log('e', e)
      }
      this.isFetchingRemove = false
    },
    parseType(type) {
      if (type === 'recover')
        return i18n.t('message.pages.historyModal.recover')
      if (type === 'delete') return i18n.t('message.pages.historyModal.delete')
      return type
    },
    parseDate(timestamp) {
      let date = new Date(timestamp)
      return (
        date.toLocaleString('pt-BR').slice(0, 3) +
        date.toLocaleString('pt-BR', { month: 'long' }).slice(0, 3) +
        '/' +
        date.toLocaleString('pt-BR').slice(6, 10)
      )
    },
    parseHours(timestamp) {
      let date = new Date(timestamp)
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    handleClose() {
      this.open = false
    },
    trimFields() {
      this.privateTokenObject.string1 = this.privateTokenObject.string1.trim()
      this.privateTokenObject.string2 = this.privateTokenObject.string2.trim()
      this.privateTokenObject.string3 = this.privateTokenObject.string3.trim()
      this.privateTokenObject.key = this.privateTokenObject.key.trim()
    },
    initialState() {
      return {
        open: false,
        edit: false,
        isFetching: false,
        isFetchingRemove: false,
        privateTokenObject: {
          drm_group_id: '',
          string1: '',
          string2: '',
          string3: '',
          key: '',
          logs: [],
          status_key: true,
        },
        tokenId: '',
        drmGroup: {
          id: '',
        },
        metadataLoading: false,
        metadataSearched: false,
        keyTab: 0,
        keyTabOptions: [{ tab: 'Editar chave' }, { tab: 'Logs' }],
      }
    },
  },
}
</script>

<style lang="scss">
.bgImg {
  background-image: url('../../assets/pandaVideoScreenShot.jpg');
  background-size: contain;
}

.pointer {
  cursor: pointer;
}
</style>
